import { type Dispatch, type SetStateAction } from "react";
import { type Observable } from "rxjs";

import { ErrorNotificationStore } from "@/stores/error-store";
import { UserStore } from "@/stores/user-store";
import {
  type BasicQuery,
  useRecoverableService,
  useService,
  useServiceBatch
} from "@/utils/hook";
import { observableFromAbortableRequest } from "@/utils/map";
import { buildQueryFromObject } from "@/utils/parser/query";
import type { RecoverableFeedSurface } from "@/utils/recoverable-request-state";
import request, { type AbortableRequest, type RequestResponse } from "@/utils/request";

import { notifyRecoverableFeedError } from "./recoverableFeedError";
import { type APIUserMeRequestDashboard } from "../models/APIUserMeRequest";
import { type APIUserMeResponse } from "../models/APIUserMeResponse";
import { type APIUserNoteRequest, type APIUserRequest } from "../models/APIUserRequest";
import {
  type APIUserNoteResponse,
  type APIUserResponse,
  type APIUserResponseNew,
  type UserDirectory
} from "../models/APIUserResponse";

export function useUserMe(): [
  RequestResponse<APIUserResponse> | undefined,
  Dispatch<SetStateAction<BasicQuery>>,
  Observable<RequestResponse<APIUserResponse>> | undefined,
  BasicQuery
] {
  return useService<APIUserResponse, BasicQuery>(
    (query) => {
      const req = request.getRequest(
        buildQueryFromObject({ _reload: query._reload }, "api/users/me?")
      )<APIUserResponse>();
      req.response.then((resp) => {
        UserStore.getInstance().add(resp.data);
      });
      return req;
    },
    { workspace__id: "" },
    (error) => {
      if (error instanceof DOMException) return;
      ErrorNotificationStore.getInstance().notifyByStatus(
        error.status,
        error.message
      );
    },
    true,
    false,
    "none"
  );
}
// The users feed backs pickers, filters and selectors: a denial is a profile
// restriction, not a failure of the screen. The header is half of the fix —
// the transport notifies before throwing — and the guard in each error handler
// is the other half.
const USERS_FEED_HEADERS = {
  "x-skip-error-notification": "true"
} as const;

/**
 * Users feed for pickers and filters.
 *
 * `onDenied` fires when the profile cannot read users, so a caller can hide the
 * control it backs instead of leaving it visible and empty. It is optional:
 * the silencing reaches every consumer, the hiding only the ones that ask.
 */
export function useUsers<Query extends BasicQuery>(
  initialQuery: Query,
  surfaceOrOnDenied: RecoverableFeedSurface | (() => void) = "secondary"
) {
  const surface =
    typeof surfaceOrOnDenied === "string" ? surfaceOrOnDenied : "secondary";
  const onDenied =
    typeof surfaceOrOnDenied === "function" ? surfaceOrOnDenied : undefined;
  return useRecoverableService(
    (query) =>
      request.getRequest(
        buildQueryFromObject(query, "api/users/?"),
        USERS_FEED_HEADERS
      )<APIUserResponse[]>(),
    initialQuery,
    (error) => {
      if (error instanceof DOMException) return;
      if (error?.status === 403) {
        onDenied?.();
        return;
      }
      notifyRecoverableFeedError(error);
    },
    { feed: "users", surface },
    true,
    "resolved"
  );
}
export type UserSelectorPageQuery = {
  page: number;
  page_size: number;
  fields: string;
  is_active?: "true";
  search?: string;
};

/** Minimal user projection for remote selectors (`fields=` keeps it light). */
export type UserSelectorRow = Pick<APIUserResponse, "id" | "name">;

/**
 * One page of the users feed for a remote picker: the caller searches and
 * paginates server-side instead of downloading the whole tenant. The central
 * request writer still appends the active workspace scope.
 */
export function getUsersSelectorPage(
  query: UserSelectorPageQuery
): AbortableRequest<UserSelectorRow[]> {
  return request.getRequest(
    buildQueryFromObject(query, "api/users/?"),
    USERS_FEED_HEADERS
  )<UserSelectorRow[]>();
}

/** Display name of one user by id, with the same minimal projection. */
export function getUserSelectorRow(
  id: number,
  fields: string
): AbortableRequest<UserSelectorRow> {
  return request.getRequest(
    buildQueryFromObject({ fields }, `api/users/${id}/?`),
    USERS_FEED_HEADERS
  )<UserSelectorRow>();
}

export function patchUsers(
  data: { groups: string[] },
  id: number
): AbortableRequest<APIUserResponse> {
  return request.patchRequest<{ groups: string[] }, APIUserResponse>(
    `api/users/${id}/`,
    data,
    {
      "content-type": "application/json"
    }
  )();
}
/**
 * Users feed for pickers and filters.
 *
 * `onDenied` fires when the profile cannot read users, so a caller can hide the
 * control it backs instead of leaving it visible and empty.
 */
export function useAllUsersInBatch<T extends BasicQuery & { page: number }>(
  query: T,
  onDenied?: () => void,
  enabled: boolean = true
): [
  APIUserResponse[] | undefined,
  Dispatch<SetStateAction<T>>,
  Observable<APIUserResponse[]> | undefined,
  T
] {
  return useServiceBatch<APIUserResponse, T>(
    "api/users/?",
    query,
    (error) => {
      if (error instanceof DOMException) return;
      if (error?.status === 403) {
        onDenied?.();
        return;
      }
      // DRF returns 404 when a batched page disappears between requests. The
      // optional picker/filter has already degraded to an empty collection.
      if (error?.status === 404) return;
      ErrorNotificationStore.getInstance().notifyByStatus(
        error?.status,
        error?.message
      );
    },
    { headers: USERS_FEED_HEADERS },
    enabled,
    "resolved"
  );
}

/**
 * Narrow user directory intended for operational pickers and filters.
 * Unlike the administrative users endpoint, this feed is governed by the
 * public `users.directory.view` capability.
 */
export function useUserDirectoryInBatch<
  T extends BasicQuery & { page: number }
>(
  query: T,
  onDenied?: () => void,
  enabled: boolean = true
): [
  UserDirectory[] | undefined,
  Dispatch<SetStateAction<T>>,
  Observable<UserDirectory[]> | undefined,
  T
] {
  return useServiceBatch<UserDirectory, T>(
    "api/users/directory/?",
    query,
    (error) => {
      if (error instanceof DOMException) return;
      if (error?.status === 403) {
        onDenied?.();
        return;
      }
      if (error?.status === 404) return;
      ErrorNotificationStore.getInstance().notifyByStatus(
        error?.status,
        error?.message
      );
    },
    { headers: USERS_FEED_HEADERS },
    enabled,
    "resolved"
  );
}
export function updateUserPreference(
  name: string,
  value: string
): Observable<unknown> {
  return observableFromAbortableRequest(
    request.patchRequest(
      `api/userpreferences/${name}/`,
      {
        value: value
      },
      {
        "content-type": "application/json"
      }
    )()
  );
}

/**
 * `reload` bypasses the in-memory GET cache: use it right after mutating the
 * profile, otherwise the cached (pre-edit) `/api/users/me` is returned.
 */
export function getUserMe(
  options: { reload?: boolean } = {}
): Observable<RequestResponse<APIUserMeResponse>> {
  const token = localStorage.getItem("token");
  request.setGlobalHeader({
    Authorization: `Token ${token}`
  });
  return observableFromAbortableRequest(
    request.getRequest(
      "api/users/me",
      undefined,
      options.reload ? "reload" : undefined
    )()
  );
}

export function getAllUsers(): Observable<
  RequestResponse<APIUserMeResponse[]>
> {
  const token = localStorage.getItem("token");
  request.setGlobalHeader({
    Authorization: `Token ${token}`
  });
  return observableFromAbortableRequest(request.getRequest("api/users")());
}

export function getUserById(
  id: string
): Observable<RequestResponse<APIUserResponseNew>> {
  const token = localStorage.getItem("token");
  request.setGlobalHeader({
    Authorization: `Token ${token}`
  });
  return observableFromAbortableRequest(
    request.getRequest(`api/users/${id}`)()
  );
}
export function updateUserMe(
  data: Partial<APIUserRequest>
): Observable<RequestResponse<APIUserResponse>> {
  return observableFromAbortableRequest(
    request.patchRequest<Partial<APIUserRequest>, APIUserResponse>(
      "api/users/me",
      data,
      {
        "content-type": "application/json"
      }
    )()
  );
}

export function updateUserMeDashboard(
  data: APIUserMeRequestDashboard
): Observable<RequestResponse<APIUserMeResponse>> {
  return observableFromAbortableRequest(
    request.patchRequest<APIUserMeRequestDashboard, APIUserMeResponse>(
      "api/users/me",
      data,
      {
        "content-type": "application/json"
      }
    )()
  );
}

export function updateUserFormData(data: FormData): Observable<unknown> {
  return observableFromAbortableRequest(
    request.patchFormDataRequest<FormData>("api/users/me", data)()
  );
}

export function changePassword(data: {
  current_password: string;
  new_password: string;
}): Observable<unknown> {
  return observableFromAbortableRequest(
    request.putRequest<
      {
        current_password: string;
        new_password: string;
      },
      APIUserMeResponse
    >("api/users/me/change_password", data, {
      "content-type": "application/json"
    })()
  );
}

export function updateAutosaveForm(autosave: boolean): Observable<unknown> {
  console.log("autosave", autosave);
  return observableFromAbortableRequest(
    request.patchRequest(
      "api/users/me",
      { autosave_form: autosave },
      {
        "content-type": "application/json"
      }
    )()
  );
}

export function useUserNotes<
  Query extends BasicQuery & { page?: number; page_size?: number }
>(initialQuery: Query, params: { user_id: string | number }) {
  return useService(
    (query) =>
      request.getRequest(
        buildQueryFromObject(
          { page: 1, page_size: 10, ...query },
          `api/users/${params.user_id}/notes/?`
        )
      )<APIUserNoteResponse[]>(),
    initialQuery,
    (error) => {
      if (error instanceof DOMException) return;
      ErrorNotificationStore.getInstance().notifyByStatus(
        error.status,
        error.message
      );
    }
  );
}

export function useUserNotesInBatch<
  Query extends BasicQuery & { page: number }
>(initialQuery: Query, params: { user_id: string | number }) {
  return useServiceBatch<APIUserNoteResponse, Query>(
    `api/users/${params.user_id}/notes/`,
    initialQuery
  );
}

export function postUserNote(
  data: APIUserNoteRequest,
  id: number | string
): AbortableRequest<APIUserNoteResponse> {
  return request.postRequest<APIUserNoteRequest, APIUserNoteResponse>(
    `api/users/${id}/notes/`,
    data,
    {
      "content-type": "application/json"
    }
  )();
}
